import { ChangeEvent, ReactElement } from "react";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlusCircle, faTimes } from "@fortawesome/free-solid-svg-icons";
import { library } from "@fortawesome/fontawesome-svg-core";

import { PageBody } from "../PageBody";
import {
  DeliveryTimeCode,
  ValidationErrors,
  Products,
  OrderClassCode,
} from "../../../types";
import { Typography } from "../../atoms/Typography";
import { Label } from "../../atoms/Label";
import { Input } from "../../atoms/Input";
import { InputDate } from "../../atoms/InputDate";
import { SelectList, SelectListItem } from "../../atoms/Select";
import { Button } from "../../atoms/Button";
import { IconTextButton } from "../../atoms/IconTextButton";
import { defaultTheme } from "../../themes";
import dayjs from "dayjs";

library.add(faPlusCircle);
library.add(faTimes);

type InputProps = {
  width?: string;
};

export type ValidatingForm = {
  name: string | undefined;
  postalCode: string | undefined;
  phoneNumber: string | undefined;
  address: string | undefined;
  deliveryDate?: string | undefined;
  deliveryTimeCode?: string | undefined;
  orderClassCode?: string | undefined;
  products?: Products[];
};

export type CreateJsonDeliveryOrderPageProps = {
  orderJson?: string;
  name?: string;
  onNameChange: (event: ChangeEvent<HTMLInputElement>) => void;
  postalCode?: string;
  onPostalCodeChange: (event: ChangeEvent<HTMLInputElement>) => void;
  phoneNumber?: string;
  onPhoneNumberChange: (event: ChangeEvent<HTMLInputElement>) => void;
  address?: string;
  onAddressChange: (event: ChangeEvent<HTMLInputElement>) => void;
  deliveryDate?: string;
  onDeliveryDateChange: (event: ChangeEvent<HTMLInputElement>) => void;
  deliveryTimeCode?: DeliveryTimeCode;
  onDeliveryTimeCodeChange: (value: number | string) => void;
  orderClassCode?: OrderClassCode;
  onOrderClassCodeChange: (value: number | string) => void;
  productsItem?: ReactElement[];
  validationErrors: ValidationErrors<ValidatingForm>;
  onCancelButtonClick: () => void;
  onSubmitClick: () => void;
  onAddProductButtonClick: () => void;
  isSubmitButtonDisable: boolean;
  handleJsonFormat: (value: any) => void;
  isClickedSubmitButton: boolean;
};

const JsonInputTextArea = styled.textarea`
  background-color: ${(props) => props.theme.colors.inputFieldBackground};
  border: 1px solid ${(props) => props.theme.colors.primaryBorder};
  border-radius: 6px;
  color: ${(props) => props.theme.colors.primaryText};
  padding: 0 40px;
  width: 100%;
  height: 100px;
`;
const ContentWrapper = styled.div<{ firstChild?: boolean }>`
  padding: 0 40px;
  clear: both;
  ${({ firstChild }) => firstChild && "margin-top: 15px;"}
`;

const InputWrapper = styled.div<InputProps>`
  width: ${(props) => props.width || "30%"};
  float: left;
  margin: 0 30px 5px 0;
`;

const InputGroupWrapper = styled.div`
  margin: 6px 0 10px 0;
  clear: both;
`;

const ButtonGroupWrapper = styled.div`
  margin: 6px 0 10px 0;
  width: auto;
  display: inline-block;
`;

const CreateButtonWrapper = styled.div`
  width: 214px;
  height: 44px;
  float: right;
  margin-bottom: 70px;
`;

export function CreateJsonDeliveryOrderPage({
  orderJson,
  name,
  onNameChange,
  postalCode,
  onPostalCodeChange,
  phoneNumber,
  onPhoneNumberChange,
  address,
  onAddressChange,
  deliveryDate,
  onDeliveryDateChange,
  deliveryTimeCode,
  onDeliveryTimeCodeChange,
  orderClassCode,
  onOrderClassCodeChange,
  productsItem,
  validationErrors,
  onCancelButtonClick,
  onSubmitClick,
  onAddProductButtonClick,
  isSubmitButtonDisable,
  handleJsonFormat,
  isClickedSubmitButton,
}: CreateJsonDeliveryOrderPageProps) {
  const isDeliveryDateValid = validationErrors.deliveryDate
    ? validationErrors.deliveryDate.isValid
    : true;

  const isDeliveryTimeCodeValid = validationErrors.deliveryTimeCode
    ? validationErrors.deliveryTimeCode.isValid
    : true;
  const isOrderClassCodeValid = validationErrors.orderClassCode
    ? validationErrors.orderClassCode.isValid
    : true;

  return (
    <>
      <PageBody
        title={"追加配送情報の入力"}
        buttonTitle="リセット"
        buttonType="danger"
        buttonOutline
        buttonIcon={
          <FontAwesomeIcon icon={faTimes} color={defaultTheme.colors.caution} />
        }
        onButtonClick={() => onCancelButtonClick()}
      >
        <>
          <ContentWrapper>
            <InputWrapper width="45%">
              <Label text={"Jsonフォーマットの入力"} margin="0 0 7px 0" />
              <JsonInputTextArea
                value={orderJson}
                onChange={handleJsonFormat}
              />
            </InputWrapper>
          </ContentWrapper>
          <ContentWrapper firstChild>
            <InputGroupWrapper>
              <InputWrapper width="45%">
                <Label text={"宛名"} margin="0 0 7px 0" />
                <Input
                  value={name}
                  error={!validationErrors.name.isValid}
                  errorMessages={validationErrors.name.message}
                  onChange={onNameChange}
                />
              </InputWrapper>
            </InputGroupWrapper>

            <InputGroupWrapper>
              <InputWrapper width="18%">
                <Label
                  text={"郵便番号"}
                  emphasis="note"
                  note="ハイフンなし"
                  margin="0 0 7px 0"
                />
                <Input
                  value={postalCode}
                  error={!validationErrors.postalCode.isValid}
                  errorMessages={validationErrors.postalCode.message}
                  onChange={onPostalCodeChange}
                />
              </InputWrapper>
              <InputWrapper width="75%">
                <Label text={"配送先住所"} margin="0 0 7px 0" />
                <Input
                  value={address}
                  error={!validationErrors.address.isValid}
                  errorMessages={validationErrors.address.message}
                  onChange={onAddressChange}
                />
              </InputWrapper>
            </InputGroupWrapper>

            <InputGroupWrapper>
              <InputWrapper width="30%">
                <Label
                  text={"電話番号"}
                  emphasis="note"
                  note="ハイフンなし"
                  margin="0 0 7px 0"
                />
                <Input
                  value={phoneNumber}
                  error={!validationErrors.phoneNumber.isValid}
                  errorMessages={validationErrors.phoneNumber.message}
                  onChange={onPhoneNumberChange}
                />
              </InputWrapper>
              <InputWrapper width="15%">
                <Label
                  text={"配送指定日"}
                  emphasis={"optional"}
                  margin="0 0 7px 0"
                />
                <InputDate
                  value={deliveryDate}
                  error={!isDeliveryDateValid}
                  errorMessages={validationErrors.deliveryDate?.message}
                  onChange={onDeliveryDateChange}
                  min={dayjs(new Date()).format("YYYY-MM-DD")}
                />
              </InputWrapper>
              <InputWrapper width="15%">
                <Label
                  text={"配送時間帯"}
                  emphasis={"optional"}
                  margin="0 0 7px 0"
                />
                <SelectList
                  placeholder={""}
                  value={deliveryTimeCode}
                  error={!isDeliveryTimeCodeValid}
                  errorMessages={validationErrors.deliveryTimeCode?.message}
                  onValueChange={onDeliveryTimeCodeChange}
                  items={[
                    <SelectListItem value="YT0000" key={0}>
                      <Typography size={2}>指定なし</Typography>
                    </SelectListItem>,
                    <SelectListItem value="YT0812" key={1}>
                      <Typography size={2}>午後中</Typography>
                    </SelectListItem>,
                    <SelectListItem value="YT1416" key={2}>
                      <Typography size={2}>14：00 ～ 16：00</Typography>
                    </SelectListItem>,
                    <SelectListItem value="YT1618" key={3}>
                      <Typography size={2}>16：00 ～ 18：00</Typography>
                    </SelectListItem>,
                    <SelectListItem value="YT1820" key={4}>
                      <Typography size={2}>18：00 ～ 20：00</Typography>
                    </SelectListItem>,
                    <SelectListItem value="YT2021" key={5}>
                      <Typography size={2}>19：00 ～ 21：00</Typography>
                    </SelectListItem>,
                  ]}
                />
              </InputWrapper>
              <InputWrapper width="25%">
                <Label
                  text={"注文クラス"}
                  emphasis={"optional"}
                  margin="0 0 7px 0"
                />
                <SelectList
                  placeholder={""}
                  value={orderClassCode}
                  error={!isOrderClassCodeValid}
                  errorMessages={validationErrors.orderClassCode?.message}
                  onValueChange={onOrderClassCodeChange}
                  items={[
                    <SelectListItem
                      value="MEDICAL_INSTITUTION_SHIPPING"
                      key={0}
                    >
                      <Typography size={2}>通常出荷（医療機関）</Typography>
                    </SelectListItem>,
                    <SelectListItem value="PATIENT_SHIPPING" key={1}>
                      <Typography size={2}>通常出荷（患者）</Typography>
                    </SelectListItem>,
                    <SelectListItem
                      value="MEDICAL_INSTITUTION_REPLACEMENT_SHIPPING"
                      key={2}
                    >
                      <Typography size={2}>代替品出荷（医療機関）</Typography>
                    </SelectListItem>,
                    <SelectListItem
                      value="PATIENT_REPLACEMENT_SHIPPING"
                      key={3}
                    >
                      <Typography size={2}>代替品出荷（患者）</Typography>
                    </SelectListItem>,
                  ]}
                />
              </InputWrapper>
            </InputGroupWrapper>
          </ContentWrapper>

          <ContentWrapper>
            <InputGroupWrapper>
              <Label text={"配送物"} margin="0 0 7px 0" />
              {productsItem}
            </InputGroupWrapper>
          </ContentWrapper>

          <ContentWrapper>
            <ButtonGroupWrapper>
              <IconTextButton
                icon={<FontAwesomeIcon icon={"plus-circle"} />}
                text={"配送物追加"}
                onButtonClick={onAddProductButtonClick}
              />
            </ButtonGroupWrapper>
          </ContentWrapper>

          <ContentWrapper>
            <CreateButtonWrapper>
              <Button
                onClick={onSubmitClick}
                buttonType={"primary"}
                disabled={isSubmitButtonDisable}
              >
                <Typography size={2} bold>
                  {isClickedSubmitButton ? "発送処理中..." : "この内容で発送"}
                </Typography>
              </Button>
            </CreateButtonWrapper>
          </ContentWrapper>
        </>
      </PageBody>
    </>
  );
}
